"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";
import { usePathname, Link } from "@/i18n/navigation";
import Button from "../ui/Button";
import { trackPhoneClick } from "../../lib/tracking";
import {
  PhoneIcon,
  ClipboardDocumentCheckIcon,
  MapPinIcon,
  SparklesIcon,
  ArrowRightIcon,
  BuildingOffice2Icon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";

export default function Footer() {
  const pathname = usePathname();
  const t = useTranslations("Footer");
  const PHONE_NUMBER_DISPLAY = "+32 (0) 493 96 65 07";
  const PHONE_NUMBER_TEL = "+32493966507";

  if (pathname?.startsWith("/admin")) return null;

  const TVA_LINK =
    "https://kbopub.economie.fgov.be/kbopub/toonondernemingps.html?ondernemingsnummer=675408030";

  const serviceLinks = [
    { key: "serviceVideMaison", link: "/vide-maison" },
    { key: "serviceDebarrasAppartement", link: "/debarras-appartement" },
    { key: "serviceDebarrasMaison", link: "/debarras-maison" },
    { key: "serviceCaveGrenierGarage", link: "/debarras-cave-grenier-garage" },
    { key: "serviceRachatObjets", link: "/rachat-objets" },
  ];

  const featuredCommunes = [
    { key: "cityIxelles", link: "/vide-maison/ixelles" },
    { key: "cityUccle", link: "/vide-maison/uccle" },
    { key: "citySchaerbeek", link: "/vide-maison/schaerbeek" },
    { key: "cityAnderlecht", link: "/vide-maison/anderlecht" },
    { key: "cityEtterbeek", link: "/vide-maison/etterbeek" },
    { key: "cityWoluweSaintPierre", link: "/vide-maison/woluwe-saint-pierre" },
  ];

  const infoLinks = [
    { key: "infoRealisations", link: "/realisations" },
    { key: "infoTarifs", link: "/tarifs" },
    { key: "infoAvisClients", link: "/avis" },
    { key: "infoBlog", link: "/blog" },
    { key: "infoContact", link: "/contact" },
  ];

  return (
    <>
      <footer className="relative mt-20 overflow-hidden bg-brand-gradient text-white">
        <div
          className="pointer-events-none absolute inset-0 opacity-80"
          style={{
            background:
              "radial-gradient(900px 420px at 10% 0%, rgba(255,255,255,0.10), transparent 55%), radial-gradient(800px 380px at 92% 8%, rgba(255,255,255,0.08), transparent 55%)",
          }}
        />

        <div className="pointer-events-none absolute inset-x-0 top-0 h-32 overflow-hidden opacity-80 md:h-40">
          <svg
            className="absolute -top-8 left-0 h-full w-[160%] sm:w-full"
            viewBox="0 0 1440 220"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path
              d="M0,96L60,101.3C120,107,240,117,360,112C480,107,600,85,720,80C840,75,960,85,1080,96C1200,107,1320,117,1380,122.7L1440,128L1440,0L0,0Z"
              fill="rgba(255,255,255,0.06)"
            />
          </svg>
          <svg
            className="absolute -top-3 left-0 h-full w-[160%] sm:w-full"
            viewBox="0 0 1440 220"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path
              d="M0,64L60,69.3C120,75,240,85,360,90.7C480,96,600,96,720,85.3C840,75,960,53,1080,48C1200,43,1320,53,1380,58.7L1440,64L1440,0L0,0Z"
              fill="rgba(255,255,255,0.10)"
            />
          </svg>
        </div>

        <div className="relative mx-auto max-w-7xl px-6 py-14 md:py-16">
          <div className="grid gap-10 lg:grid-cols-[1.2fr_0.8fr_0.9fr_0.85fr]">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-white/80">
                <SparklesIcon className="h-4 w-4 text-white/80" />
                {t("badge")}
              </div>

              <h3 className="mt-5 flex items-center gap-3 font-serif text-2xl font-extrabold tracking-tight md:text-3xl">
                <span className="flex h-24 w-24 shrink-0 items-center justify-center rounded-full bg-white p-2 shadow-sm">
                  <Image
                    src="/logo/logo-vide-maison-bruxelles-icon.png"
                    alt=""
                    width={88}
                    height={88}
                    className="h-20 w-20 rounded-full"
                  />
                </span>
                {t("brandName")}
              </h3>

              <p className="mt-4 max-w-lg text-[15px] leading-8 text-white/75 md:text-base">
                {t("description")}
              </p>

              <div className="mt-6 flex flex-wrap gap-3">
                <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-2 text-sm text-white/80">
                  <MapPinIcon className="h-4 w-4 text-white/80" />
                  {t("zonesBadge")}
                </div>

                <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-2 text-sm text-white/80">
                  <ShieldCheckIcon className="h-4 w-4 text-white/80" />
                  {t("freeQuoteBadge")}
                </div>
              </div>

              <div className="mt-7 hidden lg:flex gap-3">
                <Button
                  href={`tel:${PHONE_NUMBER_TEL}`}
                  variant="accent"
                  icon={PhoneIcon}
                  onClick={(e) => trackPhoneClick(e, { phoneNumber: PHONE_NUMBER_TEL, location: "footer_desktop" })}
                  className="h-[54px] px-5 text-sm xl:text-base"
                >
                  {PHONE_NUMBER_DISPLAY}
                </Button>

                <Button
                  href="/contact"
                  variant="sand"
                  icon={ClipboardDocumentCheckIcon}
                  className="h-[54px] px-5 text-sm xl:text-base"
                >
                  {t("requestQuote")}
                </Button>
              </div>
            </div>

            <FooterColumn title={t("servicesTitle")} links={serviceLinks} t={t} />

            <div>
              <h4 className="text-sm font-extrabold uppercase tracking-[0.14em] text-white/95">
                {t("communesTitle")}
              </h4>

              <div className="mt-5 rounded-[22px] border border-white/15 bg-white/5 p-4">
                <p className="text-sm font-semibold text-white">
                  {t("communesMainTitle")}
                </p>

                <ul className="mt-3 grid grid-cols-1 gap-2">
                  {featuredCommunes.map((commune) => (
                    <li key={commune.link}>
                      <Link
                        href={commune.link}
                        className="text-sm text-white/70 transition hover:text-white"
                      >
                        {t(commune.key)}
                      </Link>
                    </li>
                  ))}
                </ul>

                <Link
                  href="/vide-maison"
                  className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-white hover:underline"
                >
                  {t("seeAllCommunes")}
                  <ArrowRightIcon className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>

            <div>
              <FooterColumn title={t("infoTitle")} links={infoLinks} t={t} />

              <div className="mt-7 rounded-[22px] border border-white/15 bg-white/5 p-4">
                <div className="flex items-center gap-2">
                  <BuildingOffice2Icon className="h-5 w-5 text-white/80" />

                  <p className="text-sm font-semibold text-white">
                    {t("legalTitle")}
                  </p>
                </div>

                <div className="mt-3 space-y-2 text-sm leading-6 text-white/70">
                  <p>{t("brandName")}</p>

                  <a
                    href={TVA_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex text-white/75 transition hover:text-white"
                  >
                    {t("vatLabel")}
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 border-t border-white/10 pt-6 pb-24 md:pb-0">
            <div className="flex flex-col gap-4 text-sm text-white/60 md:flex-row md:items-center md:justify-between">
              <p>
                © {new Date().getFullYear()} {t("brandName")} — {t("copyright")}
              </p>

              <div className="flex flex-wrap gap-x-5 gap-y-2">
                <Link href="/contact" className="transition hover:text-white whitespace-nowrap">
                  {t("infoContact")}
                </Link>
                <Link href="/tarifs" className="transition hover:text-white">
                  {t("infoTarifs")}
                </Link>
                <Link href="/realisations" className="transition hover:text-white">
                  {t("infoRealisations")}
                </Link>
                <Link href="/blog" className="transition hover:text-white">
                  {t("infoBlog")}
                </Link>
                <Link href="/avis" className="transition hover:text-white">
                  {t("infoAvisClients")}
                </Link>
                <Link href="/politique-de-confidentialite" className="transition hover:text-white">
                  {t("privacyPolicy")}
                </Link>
                <a
                  href={TVA_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition hover:text-white"
                >
                  BCE / TVA
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      <div className="fixed bottom-0 left-0 right-0 z-[70] border-t border-white/10 bg-white/95 backdrop-blur-xl shadow-2xl md:hidden">
        <div className="grid grid-cols-2 gap-3 p-3">
          <Button
            href={`tel:${PHONE_NUMBER_TEL}`}
            variant="accent"
            icon={PhoneIcon}
            onClick={(e) => trackPhoneClick(e, { phoneNumber: PHONE_NUMBER_TEL, location: "main_mobile" })}
            className="h-[50px] w-full px-3 text-sm"
          >
            {t("call")}
          </Button>

          <Button
            href="/contact"
            variant="sand"
            icon={ClipboardDocumentCheckIcon}
            className="h-[50px] w-full px-3 text-sm"
          >
            {t("freeQuote")}
          </Button>
        </div>
      </div>
    </>
  );
}

function FooterColumn({ title, links, t }) {
  return (
    <div>
      <h4 className="text-sm font-extrabold uppercase tracking-[0.14em] text-white/95">
        {title}
      </h4>

      <ul className="mt-5 space-y-3">
        {links.map((item) => (
          <FooterLink key={item.link} item={item} t={t} />
        ))}
      </ul>
    </div>
  );
}

function FooterLink({ item, t }) {
  return (
    <li>
      <Link
        href={item.link}
        className="group inline-flex items-start gap-2 text-white/78 transition hover:text-white"
      >
        <ArrowRightIcon className="mt-0.5 h-4 w-4 shrink-0 text-white/60 transition group-hover:translate-x-0.5" />
        <span>{t(item.key)}</span>
      </Link>
    </li>
  );
}
